/**
 * NEW TASK MODAL
 *
 * Colony-styled modal for creating new tasks.
 * Supports: project selection, workflow selection, prompt input.
 * Actions: Cancel, Add to Kanban, Start Immediately.
 */

import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  X,
  ChevronDown,
  Check,
  Sparkles,
  Zap,
  FolderKanban,
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import { useWorldStore, type Task } from "@/stores/worldStore";
import { useWorkflowStore } from "@/stores/workflowStore";
import { useToast } from "@/hooks/use-toast";
import { useEscapeClose } from "@/hooks/useEscapeClose";
import {
  WorkflowSelector,
  COLONY_AGENT_SENTINEL,
  isColonyAgent,
  workflowIssueTypeToTaskIssueType,
  filterSavedWorkflows,
} from "@/components/WorkflowSelector";

// ─── Types ──────────────────
type SubmitMode = "kanban" | "immediate";
type ModalStatus = "idle" | "creating" | "done" | "failed";

interface NewTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultProjectId?: number | null;
}

// ─── Custom Select ──────────────────
function ColonySelect({
  value,
  onChange,
  options,
  placeholder,
  icon,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string; sublabel?: string }[];
  placeholder: string;
  icon?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`
          flex items-center gap-2.5 w-full h-9 px-3 rounded-[6px]
          text-[13px] text-left transition-all duration-150 cursor-pointer
          border outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--colony-brand)] focus-visible:ring-offset-1
          ${open
            ? "border-[color:var(--colony-brand)] shadow-[0_0_0_2px_var(--colony-brand-glow)]"
            : "border-[var(--colony-border)] hover:border-[var(--colony-border-hover)]"
          }
        `}
        style={{
          background: "var(--colony-bg)",
          boxShadow: open ? undefined : "var(--shadow-inset)",
          transitionTimingFunction: "var(--ease-out-expo)",
        }}
      >
        {icon && <span className="text-colony-text-tertiary shrink-0">{icon}</span>}
        <span className={`flex-1 truncate ${selected ? "text-colony-text-primary" : "text-colony-text-tertiary"}`}>
          {selected?.label || placeholder}
        </span>
        <ChevronDown
          size={13}
          strokeWidth={1.5}
          className={`text-colony-text-tertiary shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute top-full left-0 right-0 mt-1.5 z-50 rounded-[8px] overflow-hidden max-h-[220px] overflow-y-auto colony-scrollbar"
          style={{
            background: "var(--colony-surface-elevated)",
            border: "1px solid var(--colony-border)",
            boxShadow: "var(--colony-shadow-elevated)",
            backdropFilter: "var(--glass-blur)",
            animation: "colony-fadeIn 120ms var(--ease-out-expo)",
          }}
        >
          <div className="py-1">
            {options.map((option) => (
              <button
                key={option.value}
                role="option"
                aria-selected={value === option.value}
                onClick={() => { onChange(option.value); setOpen(false); }}
                className={`
                  flex items-center gap-2.5 w-full h-8 px-3 text-left transition-colors cursor-pointer
                  focus-visible:ring-1 focus-visible:ring-[color:var(--colony-brand)] focus-visible:outline-none
                  ${value === option.value
                    ? "bg-[color:var(--colony-brand-muted)] text-colony-text-primary"
                    : "text-colony-text-secondary hover:text-colony-text-primary hover:bg-[rgba(255,255,255,0.04)]"
                  }
                `}
              >
                <span className="flex-1 text-[13px] truncate">{option.label}</span>
                {option.sublabel && (
                  <span className="text-[11px] text-colony-text-tertiary shrink-0">{option.sublabel}</span>
                )}
                {value === option.value && <Check size={13} className="shrink-0" style={{ color: "var(--colony-brand)" }} />}
              </button>
            ))}
            {options.length === 0 && (
              <div className="px-3 py-3 text-[12px] text-colony-text-tertiary text-center">No options available</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Modal ──────────────────
export function NewTaskModal({ isOpen, onClose, defaultProjectId }: NewTaskModalProps) {
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(
    () => localStorage.getItem("factory:lastWorkflowId") || COLONY_AGENT_SENTINEL,
  );
  const [prompt, setPrompt] = useState("");
  const [status, setStatus] = useState<ModalStatus>("idle");
  const [submitMode, setSubmitMode] = useState<SubmitMode>("kanban");
  const [createdTask, setCreatedTask] = useState<Task | null>(null);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const projects = useWorldStore((s) => s.projects);
  const addTask = useWorldStore((s) => s.addTask);
  const startTaskPipeline = useWorldStore((s) => s.retryTaskPipeline);
  const workflows = useWorkflowStore((s) => s.workflows);
  const fetchWorkflows = useWorkflowStore((s) => s.fetchWorkflows);
  const { toast } = useToast();

  // Fetch workflows on open
  useEffect(() => {
    if (isOpen) void fetchWorkflows();
  }, [isOpen, fetchWorkflows]);

  // Focus textarea on open
  useEffect(() => {
    if (isOpen && status === "idle") {
      const timer = setTimeout(() => textareaRef.current?.focus(), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, status]);

  useEscapeClose(isOpen, onClose);

  // Auto-select project: prefer defaultProjectId, fallback to first project
  useEffect(() => {
    if (!isOpen) return;
    if (!selectedProject && projects.length > 0) {
      if (defaultProjectId != null && projects.some((p) => Number(p.id) === Number(defaultProjectId))) {
        setSelectedProject(String(defaultProjectId));
      } else {
        setSelectedProject(String(projects[0].id));
      }
    }
  }, [isOpen, projects, selectedProject, defaultProjectId]);

  const savedWorkflows = useMemo(() => filterSavedWorkflows(workflows), [workflows]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedWorkflowId((current) => {
      if (isColonyAgent(current)) return current;
      if (current && savedWorkflows.some((w) => w.id === current)) return current;
      return COLONY_AGENT_SENTINEL;
    });
  }, [isOpen, savedWorkflows]);

  // Persist last selected workflow
  useEffect(() => {
    if (selectedWorkflowId) {
      localStorage.setItem("factory:lastWorkflowId", selectedWorkflowId);
    }
  }, [selectedWorkflowId]);

  const selectedWorkflow = useMemo(
    () => savedWorkflows.find((w) => w.id === selectedWorkflowId),
    [savedWorkflows, selectedWorkflowId],
  );

  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: String(p.id), label: p.name })),
    [projects],
  );

  const canSubmit = prompt.trim().length > 0 && selectedProject && (isColonyAgent(selectedWorkflowId) || !!selectedWorkflow);

  const handleReset = useCallback(() => {
    setPrompt("");
    setSelectedProject("");
    // Keep selectedWorkflowId — handy for creating multiple tasks in a row
    setStatus("idle");
    setCreatedTask(null);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  const handleSubmit = async (mode: SubmitMode) => {
    if (!canSubmit) return;

    const resolvedProjectId = parseInt(selectedProject, 10);
    if (isNaN(resolvedProjectId)) return;

    setSubmitMode(mode);
    setStatus("creating");
    setError(null);

    try {
      const isColony = isColonyAgent(selectedWorkflowId);
      const issueType: Task["issueType"] = isColony
        ? "feature"
        : workflowIssueTypeToTaskIssueType(selectedWorkflow!.issueType);

      const title = prompt.split("\n")[0].trim().slice(0, 80);

      const result = await addTask(
        {
          projectId: resolvedProjectId,
          title,
          description: prompt,
          metadata: {
            startInput: { title, description: prompt },
            ...(isColony && { workflowType: "COLONY_AGENT" }),
          },
          pipelineStage: "backlog",
          status: "pending",
          issueType,
          ...(selectedWorkflow && { workflowId: selectedWorkflow.id }),
        },
        resolvedProjectId,
      );

      if (!result) throw new Error("Task creation returned null");

      // If immediate, start the pipeline
      if (mode === "immediate" && result.id) {
        await startTaskPipeline(String(result.id));
      }

      setCreatedTask(result);
      setStatus("done");
      toast({
        title: mode === "immediate" ? "Task started" : "Task added to Kanban",
        description: mode === "immediate"
          ? "Task created and pipeline started"
          : "Task added to backlog",
      });
    } catch (err) {
      setStatus("failed");
      const message = err instanceof Error ? err.message : "Failed to create task";
      setError(message);
      toast({
        title: "Task creation failed",
        description: message,
        variant: "destructive",
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] flex items-center justify-center"
      onClick={(e) => { if (e.target === overlayRef.current) handleClose(); }}
      style={{ animation: "colony-overlayIn 200ms var(--ease-out-expo)" }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{
          background: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(8px) saturate(0.8)",
        }}
      />

      {/* Modal */}
      <div
        className="relative w-[520px] max-h-[90vh] flex flex-col rounded-[14px] overflow-hidden"
        style={{
          background: "var(--colony-surface)",
          border: "1px solid var(--colony-border)",
          boxShadow: `
            0 0 0 1px rgba(255, 255, 255, 0.04),
            0 24px 68px rgba(0, 0, 0, 0.55),
            0 8px 20px rgba(0, 0, 0, 0.4)
          `,
          animation: "colony-modalIn 250ms var(--ease-out-expo)",
        }}
      >
        {/* ─── Header accent line ─── */}
        <div className="h-[2px] shrink-0" style={{
          background: "linear-gradient(90deg, transparent 0%, var(--colony-brand) 30%, var(--colony-brand-hover) 70%, transparent 100%)",
          opacity: 0.7,
        }} />

        {/* ─── Header ─── */}
        <div className="flex items-start gap-3.5 px-6 pt-5 pb-4">
          <div
            className="flex items-center justify-center w-10 h-10 rounded-[10px] shrink-0"
            style={{
              background: "var(--colony-brand-muted)",
              border: "1px solid rgba(0, 178, 200, 0.15)",
            }}
          >
            <Sparkles size={20} strokeWidth={1.5} style={{ color: "var(--colony-brand)" }} />
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <h2 className="text-[16px] font-semibold text-colony-text-heading tracking-[-0.015em]">
              New Task
            </h2>
            <p className="text-[12px] text-colony-text-tertiary mt-0.5">
              Create a task and assign it to a project workflow
            </p>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-7 h-7 rounded-[6px] text-colony-text-tertiary hover:text-colony-text-primary hover:bg-[rgba(255,255,255,0.06)] transition-all cursor-pointer shrink-0 mt-0.5"
          >
            <X size={15} strokeWidth={1.5} />
          </button>
        </div>

        {/* ─── Divider ─── */}
        <div className="h-px mx-6" style={{ background: "var(--colony-border)" }} />

        {/* ─── Body ─── */}
        <div className="flex-1 overflow-y-auto colony-scrollbar px-6 py-5">
          {status === "idle" && (
            <div className="flex flex-col gap-5" style={{ animation: "colony-fadeIn 200ms var(--ease-out-expo)" }}>
              {/* Project + Workflow row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[12px] font-medium text-colony-text-secondary tracking-[0.02em]">
                    Project
                  </label>
                  <ColonySelect
                    value={selectedProject}
                    onChange={setSelectedProject}
                    options={projectOptions}
                    placeholder="Select project..."
                    icon={<FolderKanban size={14} strokeWidth={1.5} />}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[12px] font-medium text-colony-text-secondary tracking-[0.02em]">
                    Workflow
                  </label>
                  <WorkflowSelector
                    value={selectedWorkflowId}
                    onChange={setSelectedWorkflowId}
                    workflows={savedWorkflows}
                  />
                </div>
              </div>

              {/* Prompt */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-medium text-colony-text-secondary tracking-[0.02em]">
                  Describe your task
                </label>
                <div className="relative">
                  <textarea
                    ref={textareaRef}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="e.g., Add a dark mode toggle to the settings page with system preference detection..."
                    rows={5}
                    className={`
                      w-full px-3.5 py-3 rounded-[8px] resize-y text-[13px] leading-[1.65]
                      text-colony-text-primary placeholder-colony-text-tertiary
                      border outline-none transition-all duration-150
                      min-h-[120px] max-h-[280px]
                    `}
                    style={{
                      background: "var(--colony-bg)",
                      borderColor: prompt ? "var(--colony-border-hover)" : "var(--colony-border)",
                      boxShadow: "var(--shadow-inset)",
                      transitionTimingFunction: "var(--ease-out-expo)",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "var(--colony-brand)";
                      e.target.style.boxShadow = "var(--shadow-inset), 0 0 0 2px var(--colony-brand-glow)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = prompt ? "var(--colony-border-hover)" : "var(--colony-border)";
                      e.target.style.boxShadow = "var(--shadow-inset)";
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canSubmit) {
                        e.preventDefault();
                        void handleSubmit("kanban");
                      }
                    }}
                  />
                  {/* Character hint */}
                  <div className="absolute bottom-2.5 right-3 text-[10px] text-colony-text-tertiary opacity-60 pointer-events-none">
                    {prompt.length > 0 && `${prompt.length} chars`}
                  </div>
                </div>
                <p className="text-[11px] text-colony-text-tertiary">
                  Be specific about the problem and expected behavior.
                  <span className="ml-1 opacity-60">Press &#x2318;&#x23CE; to add to Kanban</span>
                </p>
              </div>
            </div>
          )}

          {status === "creating" && (
            <div className="flex flex-col items-center justify-center py-12 gap-5" style={{ animation: "colony-fadeIn 200ms var(--ease-out-expo)" }}>
              <div className="relative">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{ background: "var(--colony-brand-muted)" }}
                >
                  <Loader2
                    size={28}
                    strokeWidth={1.5}
                    className="animate-spin"
                    style={{ color: "var(--colony-brand)" }}
                  />
                </div>
                {/* Pulse ring */}
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    border: "2px solid var(--colony-brand)",
                    opacity: 0.15,
                    animation: "colony-agentPulse 2s ease-in-out infinite",
                  }}
                />
              </div>
              <div className="text-center">
                <p className="text-[14px] font-medium text-colony-text-primary">
                  {submitMode === "immediate" ? "Creating & starting task..." : "Adding to Kanban..."}
                </p>
                <p className="text-[12px] text-colony-text-tertiary mt-1">
                  Setting up GitHub issue and backlog entry
                </p>
              </div>
            </div>
          )}

          {status === "done" && (
            <div className="flex flex-col items-center justify-center py-10 gap-5" style={{ animation: "colony-fadeIn 200ms var(--ease-out-expo)" }}>
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: "rgba(60, 203, 127, 0.12)" }}
              >
                <CheckCircle size={28} strokeWidth={1.5} className="text-[#3CCB7F]" />
              </div>
              <div className="text-center">
                <p className="text-[15px] font-semibold text-colony-text-primary">
                  {submitMode === "immediate" ? "Task Started" : "Added to Kanban"}
                </p>
                <p className="text-[12px] text-colony-text-tertiary mt-1">
                  {submitMode === "immediate"
                    ? "Pipeline is running — check the agent view for progress"
                    : "Task is in backlog — drag it to start when ready"}
                </p>
              </div>
              {createdTask?.githubIssueUrl && (
                <a
                  href={createdTask.githubIssueUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[12px] font-medium hover:opacity-80 transition-opacity"
                  style={{ color: "var(--colony-brand)" }}
                >
                  <ExternalLink size={13} />
                  View GitHub Issue
                </a>
              )}
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={handleReset}
                  className="h-8 px-4 rounded-[6px] text-[13px] font-medium text-colony-text-secondary hover:text-colony-text-primary transition-all cursor-pointer"
                  style={{
                    background: "var(--colony-surface-elevated)",
                    border: "1px solid var(--colony-border)",
                  }}
                >
                  Create another
                </button>
                <button
                  onClick={handleClose}
                  className="h-8 px-4 rounded-[6px] text-[13px] font-medium text-white transition-all cursor-pointer hover:opacity-90"
                  style={{
                    background: "var(--colony-brand)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12)",
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {status === "failed" && (
            <div className="flex flex-col items-center justify-center py-10 gap-5" style={{ animation: "colony-fadeIn 200ms var(--ease-out-expo)" }}>
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: "rgba(229, 83, 75, 0.12)" }}
              >
                <AlertCircle size={28} strokeWidth={1.5} className="text-[#E5534B]" />
              </div>
              <div className="text-center">
                <p className="text-[15px] font-semibold text-colony-text-primary">Something went wrong</p>
                <p className="text-[12px] text-colony-text-tertiary mt-1 max-w-[340px]">
                  {error || "The task couldn't be created. Please try again."}
                </p>
              </div>
              <button
                onClick={handleReset}
                className="h-8 px-4 rounded-[6px] text-[13px] font-medium text-colony-text-secondary hover:text-colony-text-primary transition-all cursor-pointer"
                style={{
                  background: "var(--colony-surface-elevated)",
                  border: "1px solid var(--colony-border)",
                }}
              >
                Try again
              </button>
            </div>
          )}
        </div>

        {/* ─── Footer ─── */}
        {status === "idle" && (
          <>
            <div className="h-px mx-6" style={{ background: "var(--colony-border)" }} />
            <div
              className="flex items-center justify-between gap-3 px-6 py-4"
              style={{ animation: "colony-fadeIn 200ms var(--ease-out-expo)" }}
            >
              <button
                onClick={handleClose}
                className="h-8 px-3.5 rounded-[6px] text-[13px] font-medium text-colony-text-secondary hover:text-colony-text-primary hover:bg-[rgba(255,255,255,0.04)] transition-all cursor-pointer"
                style={{ transitionTimingFunction: "var(--ease-out-expo)" }}
              >
                Cancel
              </button>

              <div className="flex items-center gap-2">
                {/* Add to Kanban */}
                <button
                  onClick={() => void handleSubmit("kanban")}
                  disabled={!canSubmit}
                  className={`
                    group/btn h-8 px-3.5 rounded-[6px] text-[13px] font-medium
                    flex items-center gap-2 transition-all duration-150 cursor-pointer
                    ${canSubmit
                      ? "text-colony-text-primary hover:translate-y-[-1px]"
                      : "text-colony-text-tertiary cursor-not-allowed opacity-50"
                    }
                  `}
                  style={{
                    background: canSubmit ? "var(--colony-surface-elevated)" : undefined,
                    border: `1px solid ${canSubmit ? "var(--colony-border-hover)" : "var(--colony-border)"}`,
                    transitionTimingFunction: "var(--ease-out-expo)",
                  }}
                >
                  <FolderKanban size={14} strokeWidth={1.5} className="shrink-0" />
                  Add to Kanban
                </button>

                {/* Start Immediately */}
                <button
                  onClick={() => void handleSubmit("immediate")}
                  disabled={!canSubmit}
                  className={`
                    group/btn h-8 px-4 rounded-[6px] text-[13px] font-medium text-white
                    flex items-center gap-2 transition-all duration-150 cursor-pointer
                    ${canSubmit
                      ? "hover:translate-y-[-1px] active:translate-y-0 active:scale-[0.98]"
                      : "cursor-not-allowed opacity-40"
                    }
                  `}
                  style={{
                    background: canSubmit ? "var(--colony-brand)" : "var(--colony-brand)",
                    boxShadow: canSubmit
                      ? "inset 0 1px 0 rgba(255,255,255,0.12), 0 1px 3px rgba(0,0,0,0.3)"
                      : undefined,
                    transitionTimingFunction: "var(--ease-out-expo)",
                  }}
                >
                  <Zap size={14} strokeWidth={1.5} className="shrink-0" />
                  Start task
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
